import React, { useState, useCallback } from "react";
import { Upload as AntdUpload } from "antd";
import Cropper from "react-easy-crop";
import { MenuIcon, UploadIcon, WarningIcon } from "assets/icons";
import Dropdown from "../dropdown";
import Tag from "../tag";
import AlertNotification from "components/CustomWidgets/alertNotification";
import "./style.scss";
import { getCroppedImg } from "utils/cropImage";
import Modal from "../modal";
import Button from "../button";
import useMediaQuery from "hooks/useMediaQuery";
import Tooltip from "../toolTip";

function Upload({
  className,
  parentClassName,
  items,
  onMenuClick,
  fileList = [],
  onChangeFileList = () => {},
  isPrimary = false,
  onSetPrimary = () => {},
  imageapproved,
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [cropModalVisible, setCropModalVisible] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const isMobile = useMediaQuery("(max-width: 991px)");

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleChange = ({ file, fileList: newFileList }) => {
    if (file.status === "uploading" && !file.cropped) {
      setImageToCrop(file.originFileObj);
      setCropModalVisible(true);
      return;
    }
    onChangeFileList(newFileList);
  };

  const customRequest = ({ file, onSuccess }) => {
    setIsUploading(true);
    setTimeout(() => {
      onSuccess("ok");
      setIsUploading(false);
    }, 1000);
  };

  const handleCrop = async () => {
    try {
      if (!imageToCrop || !croppedAreaPixels) {
        throw new Error("No image or crop area selected");
      }

      const croppedImage = await getCroppedImg(
        URL.createObjectURL(imageToCrop),
        croppedAreaPixels
      );

      const newFile = new File([croppedImage], imageToCrop.name, {
        type: "image/jpeg",
        lastModified: Date.now(),
      });

      onChangeFileList([
        {
          uid: `cropped-${Date.now()}`,
          name: newFile.name,
          status: "done",
          cropped: true,
          url: URL.createObjectURL(newFile),
          originFileObj: newFile,
          isApproved: false,
        },
      ]);

      setCropModalVisible(false);
      AlertNotification("success", `${imageToCrop.name} uploaded successfully`);
    } catch (e) {
      AlertNotification("error", `Failed to crop image: ${e.message}`);
    }
  };

  const handleMenuClick = (e) => {
    if (e.key === "1") {
      onChangeFileList([]);
      AlertNotification("warning", `Image removed`);
    }
    if (e.key === "2") {
      onSetPrimary();
    }
  };

  const modalFooter = [
    <Button
      variant="secondary"
      height={44}
      key="cancel"
      onClick={() => setCropModalVisible(false)}
    >
      Cancel
    </Button>,
    <Button
      height={44}
      key="crop"
      onClick={handleCrop}
      className="primary-button"
    >
      Crop Image
    </Button>,
  ];

  return (
    <div className={`upload-wrapper ${parentClassName}`}>
      <AntdUpload
        locale={{
          uploading: "Please wait...",
        }}
        className={`avatar-uploader ${className} ${isUploading ? "uploading" : ""}`}
        listType="picture-card"
        fileList={fileList}
        maxCount={1}
        width={150}
        height={150}
        accept=".jpg,.jpeg,.png"
        onChange={handleChange}
        customRequest={customRequest}
        showUploadList={{
          showRemoveIcon: true,
          showPreviewIcon: false,
          showDownloadIcon: false,
        }}
        openFileDialogOnClick={true}
      >
        {fileList.length < 1 && (
          <div>
            <UploadIcon />
          </div>
        )}
      </AntdUpload>

      {isPrimary && (
        <Tag className="primary-badge" variant="yellow">
          Primary
        </Tag>
      )}

      {fileList.length > 0 && (
        <div className="hover-menu">
          <div className="Tooltip-button">
            {!imageapproved && (
              <Tooltip title="This image is not approved by admin">
                <div className="Tooltip-warning-icon">
                  <WarningIcon stroke="#fff" />
                </div>
              </Tooltip>
            )}
          </div>

          <div className="hover-menu-actions">
            <Dropdown
              onClick={handleMenuClick || onMenuClick}
              label={null}
              items={items}
              icon={<MenuIcon />}
            />
          </div>
        </div>
      )}

      {/* Crop Modal */}
      <Modal
        title="Crop Image"
        isOpen={cropModalVisible}
        onOk={handleCrop}
        onCancel={() => setCropModalVisible(false)}
        okText="Crop"
        cancelText="Cancel"
        closable
        footer={modalFooter}
        wrapClassName="crop-modal-wrapper"
      >
        <div className="crop-container">
          {imageToCrop && (
            <Cropper
              image={URL.createObjectURL(imageToCrop)}
              crop={crop}
              zoom={zoom}
              aspect={4 / 6}
              // aspect={isMobile ? 6 / 6 : 4 / 6}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              cropShape="rect"
              showGrid={true}
              style={{
                containerStyle: {
                  height: "400px",
                  width: "100%",
                  position: "relative",
                },
              }}
            />
          )}
        </div>
      </Modal>
    </div>
  );
}

export default Upload;
